<template>
  <div class="documentation-type-form">
    <h2 class="documentation-type-form__title">
      {{ documentationType.baseName || documentationType.name }}
    </h2>
    <div v-if="relationSubjectLabel" class="documentation-type-form__relation">
      <span class="documentation-type-form__relation-badge">Familiar</span>
      <span class="documentation-type-form__relation-name">{{
        relationSubjectLabel
      }}</span>
    </div>
    <p
      v-else-if="documentationType.clientName"
      class="documentation-type-form__subject"
    >
      {{ documentationType.clientName }}
    </p>
    <p
      v-if="showDescription && documentationType.description"
      class="documentation-type-form__description"
    >
      {{ documentationType.description }}
    </p>

    <div v-if="documentationType.needsFileUpload" class="field">
      <label>{{ $t("files") }}</label>
      <FileUploader
        :type="resolvedUploadType"
        :uploader-id="`${resolvedDocumentationUploaderIdPrefix}${index}`"
        :upload-url="resolvedDocumentationUploadUrl"
        :persisted-files="documentationType.files"
        :allow-remove-persisted="false"
        :textbtn="$t('publish')"
        :hide-btn="true"
        class-img-upload="no_rounded"
        @files-uploaded="emitUpload('documentation-files-uploaded', $event)"
        @file-removed="emitUpload('documentation-file-removed', $event)"
        @file-selected="emitUpload('documentation-file-selected', $event)"
        @progress="emitUpload('documentation-upload-progress', $event)"
        @error="emitUpload('upload-error', $event)"
      />
    </div>

    <InlineMessage
      v-if="showN8nMessages && documentationType.n8nErrorMessage"
      severity="error"
      class="mt-3"
    >
      {{ documentationType.n8nErrorMessage }}
    </InlineMessage>
    <InlineMessage
      v-if="showN8nMessages && documentationType.n8nSuccessMessage"
      severity="success"
      class="mt-3"
    >
      {{ documentationType.n8nSuccessMessage }}
    </InlineMessage>

    <div class="field">
      <label>{{ resolvedExpirationLabel }}</label>
      <Calendar
        v-model="documentationType.expirationDate"
        :placeholder="expirationPlaceholder"
        date-format="dd/mm/yy"
        show-icon
      />
    </div>

    <div
      v-if="documentationType.inputs && documentationType.inputs.length"
      :class="inputsWrapperClass"
    >
      <div
        v-for="(input, inputIndex) in documentationType.inputs"
        :key="input.id || `${index}-${inputIndex}`"
        :class="inputColumnClass"
      >
        <template v-if="input.id">
          <div
            v-if="input.type !== 'checkbox'"
            class="documentation-type-form__input-head"
          >
            <label :for="`documentation-input-${index}-${inputIndex}`">
              {{ input.name }}
              <span v-if="input.isRequired">*</span>
            </label>
            <small v-if="input.description">{{ input.description }}</small>
          </div>

          <InputText
            v-if="input.type === 'text'"
            :id="`documentation-input-${index}-${inputIndex}`"
            v-model="input.value"
            type="text"
          />

          <InputNumber
            v-else-if="input.type === 'number'"
            :id="`documentation-input-${index}-${inputIndex}`"
            v-model="input.value"
            :max-fraction-digits="2"
            placeholder="0"
          />

          <Dropdown
            v-else-if="input.type === 'list'"
            :id="`documentation-input-${index}-${inputIndex}`"
            v-model="input.value"
            :options="input.arrayValues || []"
            :placeholder="resolvedDropdownPlaceholder"
            filter
          />

          <SelectButton
            v-else-if="input.type === 'button-list'"
            :id="`documentation-input-${index}-${inputIndex}`"
            v-model="input.value"
            :options="input.arrayValues || []"
          />

          <Calendar
            v-else-if="input.type === 'date'"
            :id="`documentation-input-${index}-${inputIndex}`"
            v-model="input.value"
            placeholder="31/12/2030"
            date-format="dd/mm/yy"
            show-icon
          />

          <div
            v-else-if="input.type === 'checkbox'"
            class="documentation-type-form__checkbox-field"
          >
            <Checkbox
              :id="`documentation-input-${index}-${inputIndex}`"
              v-model="input.value"
              binary
            />
            <label :for="`documentation-input-${index}-${inputIndex}`">{{
              input.name
            }}</label>
          </div>

          <div v-else-if="input.type === 'upload'">
            <label>{{ input.name }}</label>
            <FileUploader
              :type="resolvedUploadType"
              :uploader-id="`${resolvedInputUploaderIdPrefix}${index}-${inputIndex}`"
              :upload-url="resolvedInputUploadUrl"
              :persisted-files="input.filesPersisted"
              :allow-remove-persisted="false"
              :textbtn="$t('publish')"
              :hide-btn="true"
              class-img-upload="no_rounded"
              @files-uploaded="
                emitUpload('input-files-uploaded', {
                  inputIndex,
                  files: $event,
                })
              "
              @file-removed="
                emitUpload('input-file-removed', { inputIndex, url: $event })
              "
              @error="emitUpload('upload-error', $event)"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import InlineMessage from "primevue/inlinemessage";
import InputNumber from "primevue/inputnumber";
import SelectButton from "primevue/selectbutton";
import FileUploader from "../utils/FileUploader.vue";

export default {
  name: "DocumentationTypeForm",
  components: {
    FileUploader,
    InlineMessage,
    InputNumber,
    SelectButton,
  },
  props: {
    documentationType: { type: Object, required: true },
    index: { type: [Number, String], required: true },
    uploader: { type: Object, default: () => ({}) },
    ui: { type: Object, default: () => ({}) },
  },
  emits: ["upload"],
  computed: {
    relationSubjectLabel() {
      if (this.documentationType.relationClientName) {
        return this.documentationType.relationClientName;
      }
      if (
        this.documentationType.isRelation &&
        this.documentationType.clientName
      ) {
        return this.documentationType.clientName;
      }
      return null;
    },
    resolvedUploadType() {
      const relationClientId = this.documentationType.relationClientId;
      if (relationClientId) {
        return `client_data/${relationClientId}`;
      }
      return this.uploader.uploadType || "client-documentation";
    },
    resolvedDocumentationUploadUrl() {
      return this.uploader.documentationUploadUrl || "";
    },
    resolvedInputUploadUrl() {
      return this.uploader.inputUploadUrl || "";
    },
    resolvedDocumentationUploaderIdPrefix() {
      return (
        this.uploader.documentationUploaderIdPrefix || "documentationTypeUpload"
      );
    },
    resolvedInputUploaderIdPrefix() {
      return (
        this.uploader.inputUploaderIdPrefix || "documentationTypeInputUpload"
      );
    },
    showDescription() {
      return this.ui.showDescription !== false;
    },
    showN8nMessages() {
      return this.ui.showN8nMessages === true;
    },
    expirationPlaceholder() {
      return this.ui.expirationPlaceholder || "31/12/2030";
    },
    inputsWrapperClass() {
      return this.ui.inputsWrapperClass || "p-fluid formgrid grid";
    },
    inputColumnClass() {
      return this.ui.inputColumnClass || "field col-12";
    },
    resolvedExpirationLabel() {
      return (
        this.ui.expirationLabel || this.$t("documentation_type_expiration_date")
      );
    },
    resolvedDropdownPlaceholder() {
      return this.ui.dropdownPlaceholder || this.$t("dropdown_select");
    },
  },
  methods: {
    emitUpload(kind, payload) {
      this.$emit("upload", { kind, payload, index: this.index });
    },
  },
};
</script>

<style scoped>
.documentation-type-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.documentation-type-form__title {
  margin: 0;
  font-size: 1.1rem;
}

.documentation-type-form__relation {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.documentation-type-form__relation-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  border: 1px solid #dbeafe;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1.2;
}

.documentation-type-form__relation-name {
  color: #0f172a;
  font-size: 0.95rem;
  font-weight: 600;
}

.documentation-type-form__subject {
  margin: 0;
  color: #334155;
  font-size: 0.95rem;
  font-weight: 600;
}

.documentation-type-form__description {
  margin: 0;
  color: #5c6578;
}

.documentation-type-form__input-head {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.35rem;
}

.documentation-type-form__checkbox-field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
